import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { stateMeta, TONE_CLASSES, type StateMachine } from '@/lib/states';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/primitives';

/**
 * The state badge — one component covering all 16 states of the two SAD §4 machines, plus the
 * review-case lifecycle.
 *
 * Three deliberate properties:
 *
 * 1. **`machine` is required.** `RESERVED` and `EXPIRED` exist in *both* machines and mean
 *    different things (a reserved deposit is a claim; a reserved withdrawal is a claimed slot).
 *    Forcing the caller to say which machine it is removes a whole class of confusing UI.
 * 2. **Never colour alone.** Every badge carries a state-specific icon and its own label, so it
 *    is legible in greyscale and to colour-blind operators (WCAG 1.4.1). Colour is the fastest
 *    channel, not the only one.
 * 3. **Explains itself.** The badge is a tooltip trigger describing what the state means and what
 *    happens next, taken from `lib/states.ts`. An operator should never have to consult the SAD
 *    to understand a row in a table.
 */

/** Props for {@link StateBadge}. */
export interface StateBadgeProps {
  /** Which state machine `status` belongs to. */
  machine: StateMachine;
  /** The state value, e.g. `UNDER_REVIEW`. */
  status: string;
  /** Compact rendering for dense tables. */
  size?: 'sm' | 'md' | undefined;
  /** Suppress the explanatory tooltip (use inside an existing tooltip or a static export). */
  withTooltip?: boolean | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** A colour-and-icon coded badge for any state in the system. */
export function StateBadge({
  machine,
  status,
  size = 'md',
  withTooltip = true,
  className,
}: StateBadgeProps): ReactNode {
  const { t } = useTranslation();
  const meta = stateMeta(machine, status);
  const tone = TONE_CLASSES[meta.tone];
  const Icon = meta.icon;
  const label = meta.labelKey ? t(meta.labelKey) : (meta.rawLabel ?? '');

  const badge = (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-full font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-2xs' : 'px-2.5 py-1 text-xs',
        tone.badge,
        className,
      )}
      data-state={status}
      data-tone={meta.tone}
    >
      <Icon className={size === 'sm' ? 'size-3' : 'size-3.5'} aria-hidden="true" />
      <span className="truncate">{label}</span>
      {meta.terminal ? <span className="sr-only"> (terminal state)</span> : null}
    </span>
  );

  if (!withTooltip) return badge;

  return (
    <Tooltip
      content={
        <span>
          <span className="font-semibold">{label}</span>
          {meta.terminal ? <span className="text-muted-foreground"> · terminal</span> : null}
          <br />
          {t(meta.descriptionKey)}
        </span>
      }
    >
      <button type="button" className="cursor-help rounded-full text-left align-middle">
        {badge}
      </button>
    </Tooltip>
  );
}

/** Props for {@link StateDot}. */
export interface StateDotProps {
  /** Which state machine `status` belongs to. */
  machine: StateMachine;
  /** The state value. */
  status: string;
  /** Additional classes. */
  className?: string | undefined;
}

/** A bare tone dot, for use inside a cell that already names the state. */
export function StateDot({ machine, status, className }: StateDotProps): ReactNode {
  const meta = stateMeta(machine, status);
  return (
    <span
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        TONE_CLASSES[meta.tone].dot,
        className,
      )}
      aria-hidden="true"
    />
  );
}
